import { getWebPath } from '@doss/common-all'
import { normalizeLegacySystemEventFailureData, systemEvents } from '@doss/common-all/system-events'
import { Box, Chip, Stack, Theme, Typography } from '@mui/material'
import _ from 'lodash'
import { IconType } from 'react-icons'
import { FiEdit3, FiMessageSquare } from 'react-icons/fi'
import { RxArrowRight, RxCrossCircled, RxDotFilled, RxTrash } from 'react-icons/rx'
import {
  TbChartBar,
  TbColumns,
  TbEraser,
  TbRowInsertTop,
  TbTable,
  TbTableColumn,
  TbTableImport,
  TbTableOff,
  TbTablePlus,
} from 'react-icons/tb'
import { z } from 'zod'
import {
  DataType,
  IndexType,
  SystemEventStatus,
  SystemEventType,
} from '~/common/generated/apollo-operations'
import { TranslationKeys } from '~/common/generated/i18n.generated'
import { ActivityComment } from '~/components/activity/ActivityTimeline/ActivityComment'
import {
  ActivityEventItemType,
  DossActivityTimelineStyles,
} from '~/components/activity/ActivityTimeline/ActivityTimeline'
import {
  PaginatedComment,
  PaginatedCommentAuthor,
  PaginatedEvent,
  PaginatedNotificationEvent,
} from '~/components/activity/types'
import { Tooltip } from '~/components/ui/atoms/tooltip/Tooltip'
import { purple } from '../styles/colors'
import { getNotificationItemProps } from './notificationEvent'
import { AllowedArrayInnerTypes, getHumanReadableTableColumnDataType } from './table-column'

type FeedEvent = PaginatedEvent['event']
type Translate = (key: TranslationKeys, options?: Record<string, unknown>) => string

/**
 * Renders one entry of the activity feed, which reads the system_events log.
 *
 * Payloads are validated against the same catalog the writers describe them with
 * (@doss/common-all/system-events), so a shape change surfaces here as an unrendered detail rather
 * than as a wrong one. The event type alone decides the copy; a FAIL event reuses it and adds the
 * failure chip below, so no event type needs a second branch for its unhappy path.
 */

const payload = <TSchema extends z.ZodType>(
  schema: TSchema,
  data: unknown,
): z.infer<TSchema> | undefined => {
  const result = schema.safeParse(data)
  return result.success ? result.data : undefined
}

const failureMessage = (data: unknown): string | undefined => {
  if (data === null || typeof data !== 'object' || Array.isArray(data)) {
    return undefined
  }
  // Old API responses and cached feed data can still use the legacy failure fields.
  const failure = normalizeLegacySystemEventFailureData(data as Record<string, unknown>).failure
  const message = (failure as Record<string, unknown> | null)?.message
  return typeof message === 'string' && message !== '' ? message : undefined
}

/** A changed value as a chip's label. Scalars read as themselves; everything else as its JSON. */
const formatChangeValue = (value: unknown): string | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined
  }
  return typeof value === 'string' ? value : JSON.stringify(value)
}

/** A value chip; the tooltip is what makes a truncated value readable. */
const ValueChip = ({ label, styles }: { label: string; styles: DossActivityTimelineStyles }) => (
  <Tooltip arrow title={label}>
    <Chip label={label} sx={styles.events.updateTableCell.chips} />
  </Tooltip>
)

/** The `changes` map every update event in the catalog carries. */
type SystemEventChanges = Record<string, { before: unknown; after: unknown }>

interface ChangeRowProps {
  label: string
  before: unknown
  after: unknown
  styles: DossActivityTimelineStyles
  theme: Theme
  t: Translate
}

const ChangeRow = ({ label, before, after, styles, theme, t }: ChangeRowProps) => {
  const beforeLabel = formatChangeValue(before)
  const afterLabel = formatChangeValue(after)
  const emptyText = (
    <Typography color={theme.palette.common.doss.gray[8]} variant="body2">
      {t('events.details.empty')}
    </Typography>
  )

  return (
    <Stack
      alignItems="center"
      direction="row"
      display="flex"
      gap={theme.spacing(2)}
      sx={styles.events.updateTableCell.stack}
    >
      <Typography color={theme.palette.common.doss.gray[8]} fontWeight={600} variant="caption">
        {label}
      </Typography>
      {beforeLabel ? <ValueChip label={beforeLabel} styles={styles} /> : emptyText}
      <RxArrowRight color={theme.palette.common.doss.gray[8]} fontSize="20px" />
      {afterLabel ? <ValueChip label={afterLabel} styles={styles} /> : emptyText}
    </Stack>
  )
}

// A column's type is spread across three fields the writer reports separately; the shared
// formatter spells the three as the one name the rest of the app shows for a column type.
const COLUMN_TYPE_FIELDS = ['type', 'innerType', 'indexType']

/**
 * A column update, whose type change reads as one row rather than three.
 *
 * Only possible when `type` itself changed: the formatter needs a DataType, and `changes` carries
 * a field only when it moved. An inner or index type that moved on its own falls through to the
 * generic rows below.
 */
const renderColumnChanges = (
  changes: SystemEventChanges,
  styles: DossActivityTimelineStyles,
  theme: Theme,
  t: Translate,
): JSX.Element => {
  const byField = new Map(Object.entries(changes))
  const typeChange = byField.get('type')
  if (typeChange === undefined) {
    return renderChanges(changes, styles, theme, t)
  }

  const typeLabel = (side: 'before' | 'after'): string =>
    getHumanReadableTableColumnDataType(
      t,
      typeChange[side] as DataType,
      (byField.get('innerType')?.[side] ?? null) as AllowedArrayInnerTypes | null,
      byField.get('indexType')?.[side] as IndexType,
    )

  const rest = _.omit(changes, COLUMN_TYPE_FIELDS)
  return (
    <Stack spacing={theme.spacing(0.5)}>
      <ChangeRow
        after={typeLabel('after')}
        before={typeLabel('before')}
        label={changeLabel(t, 'type')}
        styles={styles}
        t={t}
        theme={theme}
      />
      {Object.keys(rest).length > 0 ? renderChanges(rest, styles, theme, t) : null}
    </Stack>
  )
}

// Fields the feed already has copy for. Anything else falls back to its own name in title case,
// so the catalog can add a field without the feed needing a new key first.
const CHANGE_FIELD_LABELS: Partial<Record<string, TranslationKeys>> = {
  name: 'events.details.name',
  type: 'events.details.type',
}

const changeLabel = (t: Translate, field: string): string => {
  const key = CHANGE_FIELD_LABELS[field]
  return key === undefined ? _.startCase(field) : t(key)
}

// Changes are keyed by the model's own field name, which names the row it renders.
const renderChanges = (
  changes: SystemEventChanges,
  styles: DossActivityTimelineStyles,
  theme: Theme,
  t: Translate,
): JSX.Element => (
  <Stack spacing={theme.spacing(0.5)}>
    {Object.entries(changes).map(([field, change]) => (
      <ChangeRow
        key={field}
        after={change.after}
        before={change.before}
        label={changeLabel(t, field)}
        styles={styles}
        t={t}
        theme={theme}
      />
    ))}
  </Stack>
)

/** A labelled chip, the feed's way of naming the thing an event acted on. */
const NamedChip = ({
  name,
  icon: Icon,
  styles,
  theme,
}: {
  name: string
  icon: IconType
  styles: DossActivityTimelineStyles
  theme: Theme
}) => (
  <Chip
    label={
      <Stack alignItems="center" direction="row" display="flex" gap={1} justifyContent="flex-start">
        <Box alignItems="center" display="flex">
          <Icon color={theme.palette.common.doss.gray[10]} fontSize="16px" />
        </Box>
        <Typography>{name}</Typography>
      </Stack>
    }
    sx={styles.events.statusChip}
  />
)

interface EventComponents {
  icon: JSX.Element
  details: JSX.Element
  action: string
}

// The happy-path rendering for one event type. A FAIL row comes through here too, for its copy;
// its details are replaced by the caller, and the payload it carries fails every parse below.
function getComponentsForType(
  t: Translate,
  event: FeedEvent,
  styles: DossActivityTimelineStyles,
  theme: Theme,
): EventComponents {
  const empty: EventComponents = { icon: <></>, details: <></>, action: '' }

  switch (event.eventType) {
    case SystemEventType.CREATE_TABLE: {
      return {
        icon: <TbTableImport color={theme.palette.common.doss.green[9]} fontSize="16px" />,
        details: <></>,
        action: t('events.action.created_table'),
      }
    }
    case SystemEventType.UPDATE_TABLE: {
      const data = payload(systemEvents.tableUpdateSuccess.dataSchema, event.data)
      return {
        icon: <FiEdit3 color={theme.palette.common.doss.blue[9]} fontSize="16px" />,
        details: data ? renderChanges(data.changes, styles, theme, t) : <></>,
        action: t('events.action.updated_table'),
      }
    }
    case SystemEventType.DELETE_TABLE: {
      return {
        icon: <TbTableOff color={theme.palette.common.doss.red[10]} fontSize="16px" />,
        details: <></>,
        action: t('events.action.deleted_table'),
      }
    }
    case SystemEventType.CLEAR_TABLE: {
      const data = payload(systemEvents.tableClearSuccess.dataSchema, event.data)
      return {
        icon: <TbEraser color={theme.palette.common.doss.red[10]} fontSize="16px" />,
        details: data ? (
          <Chip
            label={t('events.details.records_deleted', { count: data.rows_deleted })}
            sx={styles.events.statusChip}
          />
        ) : (
          <></>
        ),
        action: t('events.action.cleared_table'),
      }
    }
    case SystemEventType.CREATE_TABLE_ROW: {
      const data = payload(systemEvents.tableRowCreateSuccess.dataSchema, event.data)
      return {
        icon: <TbRowInsertTop color={theme.palette.common.doss.green[9]} fontSize="16px" />,
        details: (
          <NamedChip
            icon={TbColumns}
            name={data?.row_name ?? t('events.details.new_record')}
            styles={styles}
            theme={theme}
          />
        ),
        action: t('events.action.created_record'),
      }
    }
    case SystemEventType.DELETE_TABLE_ROW: {
      const data = payload(systemEvents.tableRowDeleteSuccess.dataSchema, event.data)
      return {
        icon: <RxTrash color={theme.palette.common.doss.red[10]} fontSize="16px" />,
        details: data?.row_name ? (
          <NamedChip icon={TbColumns} name={data.row_name} styles={styles} theme={theme} />
        ) : (
          <></>
        ),
        action: t('events.action.deleted_record'),
      }
    }
    case SystemEventType.UPDATE_TABLE_CELL: {
      const data = payload(systemEvents.tableCellUpdateSuccess.dataSchema, event.data)
      return {
        icon: <FiEdit3 color={theme.palette.common.doss.blue[9]} fontSize="16px" />,
        details: data ? (
          <ChangeRow
            after={data.after}
            before={data.before}
            label={data.column_name ?? ''}
            styles={styles}
            t={t}
            theme={theme}
          />
        ) : (
          <></>
        ),
        action: t('events.action.updated_table_cell', {
          changedColumnName: data?.column_name ?? t('events.details.a_field'),
        }),
      }
    }
    case SystemEventType.CREATE_TABLE_COLUMN: {
      const data = payload(systemEvents.tableColumnCreateSuccess.dataSchema, event.data)
      return {
        icon: <TbTableColumn color={theme.palette.common.doss.green[9]} fontSize="16px" />,
        details: data?.name ? (
          <NamedChip icon={TbTableColumn} name={data.name} styles={styles} theme={theme} />
        ) : (
          <></>
        ),
        action: t('events.action.created_table_column'),
      }
    }
    case SystemEventType.UPDATE_TABLE_COLUMN: {
      const data = payload(systemEvents.tableColumnUpdateSuccess.dataSchema, event.data)
      return {
        icon: <FiEdit3 color={theme.palette.common.doss.blue[9]} fontSize="16px" />,
        details: data ? renderColumnChanges(data.changes, styles, theme, t) : <></>,
        action: t('events.action.updated_table_column', { columnName: data?.name ?? '' }),
      }
    }
    case SystemEventType.DELETE_TABLE_COLUMN: {
      const data = payload(systemEvents.tableColumnDeleteSuccess.dataSchema, event.data)
      return {
        icon: <RxTrash color={theme.palette.common.doss.red[10]} fontSize="16px" />,
        details: data?.name ? (
          <NamedChip icon={TbTableColumn} name={data.name} styles={styles} theme={theme} />
        ) : (
          <></>
        ),
        action: t('events.action.deleted_table_column'),
      }
    }
    case SystemEventType.CREATE_TABLE_TAB: {
      const data = payload(systemEvents.tableTabCreateSuccess.dataSchema, event.data)
      return {
        icon: <TbTablePlus color={theme.palette.common.doss.green[9]} fontSize="16px" />,
        details: data?.name ? (
          <NamedChip icon={TbTable} name={data.name} styles={styles} theme={theme} />
        ) : (
          <></>
        ),
        action: t('events.action.created_view'),
      }
    }
    case SystemEventType.UPDATE_TABLE_TAB: {
      const data = payload(systemEvents.tableTabUpdateSuccess.dataSchema, event.data)
      return {
        icon: <FiEdit3 color={theme.palette.common.doss.blue[9]} fontSize="16px" />,
        details: data ? renderChanges(data.changes, styles, theme, t) : <></>,
        action: t('events.action.updated_view'),
      }
    }
    case SystemEventType.DELETE_TABLE_TAB: {
      const data = payload(systemEvents.tableTabDeleteSuccess.dataSchema, event.data)
      return {
        icon: <RxTrash color={theme.palette.common.doss.red[10]} fontSize="16px" />,
        details: data?.name ? (
          <NamedChip icon={TbTable} name={data.name} styles={styles} theme={theme} />
        ) : (
          <></>
        ),
        action: t('events.action.deleted_view'),
      }
    }
    case SystemEventType.UPLOAD_CSV: {
      const data = payload(systemEvents.csvUploadSuccess.dataSchema, event.data)
      return {
        icon: <TbTableImport color={theme.palette.common.doss.green[9]} fontSize="16px" />,
        details: data ? (
          <Stack direction="row" gap={1}>
            <Chip
              label={t('events.details.records_created', { count: data.rows_created ?? 0 })}
              sx={styles.events.statusChip}
            />
            <Chip
              label={t('events.details.records_updated', { count: data.rows_updated ?? 0 })}
              sx={styles.events.statusChip}
            />
          </Stack>
        ) : (
          <></>
        ),
        action: t('events.action.uploaded_csv'),
      }
    }
    case SystemEventType.CREATE_CHART: {
      const data = payload(systemEvents.chartCreateSuccess.dataSchema, event.data)
      return {
        icon: <TbChartBar color={theme.palette.common.doss.green[9]} fontSize="16px" />,
        details: <></>,
        action: t('events.action.created_chart', { chartName: data?.name ?? '' }),
      }
    }
    case SystemEventType.UPDATE_CHART: {
      const data = payload(systemEvents.chartUpdateSuccess.dataSchema, event.data)
      return {
        icon: <TbChartBar color={theme.palette.common.doss.blue[9]} fontSize="16px" />,
        details: data ? renderChanges(data.changes, styles, theme, t) : <></>,
        action: t('events.action.updated_chart'),
      }
    }
    case SystemEventType.DELETE_CHART: {
      const data = payload(systemEvents.chartDeleteSuccess.dataSchema, event.data)
      return {
        icon: <TbChartBar color={theme.palette.common.doss.red[10]} fontSize="16px" />,
        details: <></>,
        action: t('events.action.deleted_chart', { chartName: data?.name ?? '' }),
      }
    }
    default:
      return empty
  }
}

/**
 * Icon, copy and detail for one feed entry.
 *
 * An event type with no case of its own still renders: the feed reads a log that grows with the
 * catalog, and a silent gap in a timeline reads as nothing having happened.
 */
function getEventComponents(
  t: Translate,
  paginatedEvent: PaginatedEvent,
  styles: DossActivityTimelineStyles,
  theme: Theme,
): EventComponents {
  const event = paginatedEvent.event
  const components = getComponentsForType(t, event, styles, theme)
  const action =
    components.action === ''
      ? t('events.action.unknown', { eventType: _.lowerCase(event.eventType) })
      : components.action
  const icon =
    components.action === '' ? (
      <RxDotFilled color={theme.palette.common.doss.gray[9]} fontSize="16px" />
    ) : (
      components.icon
    )

  if (event.eventStatus !== SystemEventStatus.FAIL) {
    return { ...components, action, icon }
  }

  // Failures share their type's copy; what differs is that it did not happen, which the chip says
  // and the payload's customer-safe message explains.
  const message = failureMessage(event.data)
  return {
    icon: <RxCrossCircled color={theme.palette.common.doss.red[10]} fontSize="16px" />,
    action,
    details: (
      <Stack alignItems="flex-start" direction="column" display="flex" gap={1}>
        <Chip
          label={t('events.details.failed')}
          sx={{ ...styles.events.statusChip, ...styles.events.statusChip.failure }}
        />
        {message ? (
          <Typography
            color={theme.palette.common.doss.gray[9]}
            sx={styles.events.statusChip.errorMessage}
            variant="body2"
          >
            {message}
          </Typography>
        ) : null}
      </Stack>
    ),
  }
}

// Sentinels the writers use where no person is named, rather than display names.
const NAMELESS_ORIGINATORS: Partial<Record<string, TranslationKeys>> = {
  NO_CONTEXT: 'events.user.system',
  ANONYMOUS: 'events.user.anonymous',
}

/**
 * Who the entry is attributed to.
 *
 * The originator is the human or system the work is traced back to; an agent or a borrowed
 * identity is named alongside it rather than in place of it, so a feed never credits an action to
 * someone who did not set it off.
 */
const getActorName = (t: Translate, event: FeedEvent): string => {
  const sentinel = NAMELESS_ORIGINATORS[event.originatorName]
  const name = sentinel === undefined ? event.originatorName : t(sentinel)

  if (event.impersonatedUserName) {
    return t('events.user.impersonated', { name, impersonated: event.impersonatedUserName })
  }
  if (event.agentId) {
    return t('events.user.agent', { name, agent: _.startCase(_.lowerCase(event.agentId)) })
  }
  return name
}

interface ActivityBreadcrumb {
  name: string
  link: string
}

export type ActivityItemProps = {
  user: string
  action: string
  icon: JSX.Element
  breadcrumbs: ActivityBreadcrumb[]
  details: JSX.Element
  time: string
  type: ActivityEventItemType
  author?: PaginatedCommentAuthor
}

export function getActivityItemProps(
  t: Translate,
  activity: PaginatedEvent | PaginatedComment | PaginatedNotificationEvent,
  styles: DossActivityTimelineStyles,
  theme: Theme,
): ActivityItemProps {
  // Push notifications still carry a legacy Prisma Event; the feed carries a system event.
  if (activity.__typename === 'PaginatedEvent') {
    return getNotificationItemProps(t, activity, styles, theme)
  }

  if (activity.__typename === 'PaginatedActivityFeedEvent') {
    const { action, icon, details } = getEventComponents(t, activity, styles, theme)
    return {
      type: ActivityEventItemType.CRUD,
      user: getActorName(t, activity.event),
      action,
      icon,
      breadcrumbs: [
        {
          name: activity.objectName,
          link: getWebPath({ objectId: activity.objectId, objectType: activity.objectType }),
        },
      ],
      details,
      time: activity.event.timestamp,
    }
  }

  return {
    type: ActivityEventItemType.COMMENT,
    author: activity.comment.author,
    user: `${activity.comment.author.firstName || ''} ${activity.comment.author.lastName || ''}`,
    action: t('events.action.commented'),
    icon: <FiMessageSquare color={purple[9]} fontSize="16px" />,
    breadcrumbs: [
      {
        name: activity.objectName,
        link: getWebPath({ objectId: activity.objectId, objectType: activity.objectType }),
      },
    ],
    details: <ActivityComment comment={activity} />,
    time: activity.comment.createdAt,
  }
}
